import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import DialogFormFooter from '@/components/DialogFormFooter';
import AddressEditor from '@/components/entities/AddressEditor';
import api from '@/lib/api';
import { toast } from 'sonner';
import { v4 as uuidv4 } from 'uuid';

const emptyForm = () => ({ id: '', name: '', email: '', phone: '', notes: '', addresses: [] });

const toForm = (p) => ({
  id: p.id, name: p.name || '', email: p.email || '', phone: p.phone || '',
  notes: p.notes || '', addresses: (p.addresses || []).map((a) => ({ ...a })),
});

/**
 * Create/edit a Person. Reused by the People directory page and by PersonPicker's
 * inline "new person" action. Calls onSaved(person) with the persisted record.
 */
const PersonFormDialog = ({ open, onOpenChange, person = null, onSaved, initialName = '' }) => {
  const [form, setForm] = useState(emptyForm());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(person ? toForm(person) : { ...emptyForm(), name: initialName });
  }, [open, person, initialName]);

  const save = async () => {
    setSubmitting(true);
    try {
      const payload = {
        id: person?.id || uuidv4(),
        name: form.name.trim(),
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        notes: form.notes.trim() || null,
        addresses: form.addresses,
      };
      const res = person
        ? await api.put(`/persons/${payload.id}`, payload)
        : await api.post('/persons', payload);
      toast.success(person ? 'Person updated' : 'Person created');
      onOpenChange(false);
      onSaved?.(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save person');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-bold text-primary">
            {person ? 'Edit Person' : 'New Person'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4" data-testid="person-form">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-1">
              <Label htmlFor="pf-name">Name *</Label>
              <Input id="pf-name" data-testid="pf-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="pf-email">Email</Label>
              <Input id="pf-email" data-testid="pf-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="pf-phone">Phone</Label>
              <Input id="pf-phone" data-testid="pf-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
          </div>
          <AddressEditor value={form.addresses} onChange={(addresses) => setForm({ ...form, addresses })} label="Addresses" />
          <div>
            <Label htmlFor="pf-notes">Notes</Label>
            <Textarea id="pf-notes" data-testid="pf-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
          </div>
          <DialogFormFooter
            onCancel={() => onOpenChange(false)}
            onSubmit={save}
            submitLabel={person ? 'Update Person' : 'Create Person'}
            disabled={!form.name.trim() || submitting}
            submitTestId="save-person-btn"
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PersonFormDialog;
